/**
 * UnsubscribePage – polished cancel-auto-renewal flow.
 *
 * Flow (3 stages, single page):
 *   1. Confirm intent — what you keep until expiry, what changes after
 *   2. Optional reason chips + free-text feedback
 *   3. Confirmation screen — date, reactivate CTA
 *
 * Routes:
 *   /unsubscribe             — auth-required, the user is already logged in
 *
 * Required for Apple App Store and Google Play compliance: every paid app
 * must offer a discoverable, in-app way to cancel auto-renewal. We link to
 * this page from Profile, Pricing, and the home menu.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CaretLeft,
  ShieldCheck,
  WarningCircle,
  CheckCircle,
  Smiley,
  XCircle,
  SpinnerGap,
  ArrowRight,
  Calendar,
} from '@phosphor-icons/react';
import { http, apiErrorMessage } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const REASONS = [
  { id: 'too_expensive', label: 'Too expensive' },
  { id: 'no_time', label: "Don't have enough time" },
  { id: 'not_useful', label: "Didn't find it useful" },
  { id: 'found_alt', label: 'Found another app' },
  { id: 'temporary', label: 'Just taking a break' },
  { id: 'other', label: 'Other' },
];

export default function UnsubscribePage() {
  const { user, patchUser } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState('confirm'); // confirm | reason | done
  const [subscription, setSubscription] = useState(null);
  const [reasonId, setReasonId] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get('/subscriptions/me');
        setSubscription(data);
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const expiresAt = useMemo(() => {
    const next = subscription?.next_billing_time;
    if (!next) return null;
    return new Date(next).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  }, [subscription]);

  const isActive = !!subscription?.subscription_id && subscription?.status !== 'CANCELLED';

  async function submitCancel() {
    setSubmitting(true);
    setError('');
    try {
      const reason = REASONS.find((r) => r.id === reasonId)?.label || null;
      const { data } = await http.post('/subscriptions/cancel', {
        reason,
        feedback: feedback?.trim() || null,
      });
      patchUser(data);
      setStage('done');
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  // Already cancelled / never subscribed
  if (!loading && !isActive && stage !== 'done') {
    return (
      <div className="lf-shell flex flex-col px-6 py-10" data-testid="unsubscribe-page">
        <BackBar />
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="lf-card mt-10 p-6 text-center"
          data-testid="unsubscribe-no-sub"
        >
          <div className="grid h-14 w-14 mx-auto place-items-center rounded-full bg-[var(--lf-surface-muted)] text-[var(--lf-primary)]">
            <ShieldCheck size={28} weight="duotone" />
          </div>
          <div className="mt-4 text-xl font-bold">You're not subscribed</div>
          <p className="mt-1 text-sm text-[var(--lf-text-muted)]">
            There's no active auto-renewal on your account. You're on the Free plan.
          </p>
          <button
            onClick={() => navigate('/home')}
            className="lf-btn lf-btn-primary mt-5"
            data-testid="unsubscribe-go-home"
          >
            Back to Home <ArrowRight size={16} weight="bold" />
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="lf-shell flex flex-col px-6 py-8" data-testid="unsubscribe-page">
      <BackBar />

      {error ? (
        <div className="mt-3 text-sm text-[var(--lf-destructive)]" data-testid="unsubscribe-error">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-sm text-[var(--lf-text-muted)]">
          <SpinnerGap className="animate-spin" size={18} /> Loading your subscription…
        </div>
      ) : stage === 'confirm' ? (
        <ConfirmStage
          subscription={subscription}
          expiresAt={expiresAt}
          onContinue={() => setStage('reason')}
          onKeep={() => navigate('/profile')}
        />
      ) : stage === 'reason' ? (
        <ReasonStage
          expiresAt={expiresAt}
          reasonId={reasonId}
          setReasonId={setReasonId}
          feedback={feedback}
          setFeedback={setFeedback}
          submitting={submitting}
          onBack={() => setStage('confirm')}
          onSubmit={submitCancel}
        />
      ) : (
        <DoneStage expiresAt={expiresAt} onHome={() => navigate('/home')} />
      )}
    </div>
  );
}

// ------------------------------------------------------------------------- //
function BackBar() {
  return (
    <div className="flex items-center gap-2">
      <Link
        to="/profile"
        className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]"
        data-testid="unsubscribe-back"
      >
        <CaretLeft size={18} />
      </Link>
      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
          Manage subscription
        </div>
        <h1 className="text-2xl font-bold">Cancel auto-renewal</h1>
      </div>
    </div>
  );
}

function ConfirmStage({ subscription, expiresAt, onContinue, onKeep }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="unsubscribe-stage-confirm"
    >
      <div className="lf-card mt-5 p-5 bg-gradient-to-br from-[#FFFFFF] to-[#FFF5EE]">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-[var(--lf-surface)] text-[var(--lf-primary)]">
            <Calendar size={20} weight="duotone" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-[var(--lf-primary)]">Pro plan</div>
            <div className="text-base font-semibold">
              {subscription?.status === 'ACTIVE' ? 'Auto-renewing' : (subscription?.status || 'Active')}
            </div>
            {expiresAt ? (
              <div className="text-sm text-[var(--lf-text-muted)]">
                Next charge would be on <strong>{expiresAt}</strong>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <h2 className="mt-6 text-lg font-bold">Here's what happens if you cancel</h2>
      <ul className="mt-3 space-y-2.5" data-testid="unsubscribe-impact-list">
        <ImpactRow
          good
          icon={<CheckCircle size={18} weight="fill" />}
          title="You keep Pro access until your period ends"
          copy={expiresAt ? `Full access through ${expiresAt}.` : 'Full access until your current period ends.'}
        />
        <ImpactRow
          good
          icon={<CheckCircle size={18} weight="fill" />}
          title="You won't be charged again"
          copy="We immediately stop the next PayPal payment. No surprise renewal."
        />
        <ImpactRow
          icon={<WarningCircle size={18} weight="fill" />}
          title="After your period ends, you'll move to the Free plan"
          copy="Two daily Free Talk sessions, five lifetime scenarios, and basic vocabulary tools."
        />
        <ImpactRow
          icon={<WarningCircle size={18} weight="fill" />}
          title="Your saved progress stays"
          copy="Vocabulary, sessions, streaks, skill snapshots — all preserved. Resubscribe anytime to unlock unlimited use again."
        />
      </ul>

      <div className="mt-8 space-y-3">
        <button
          onClick={onContinue}
          className="lf-btn lf-btn-ghost w-full text-[var(--lf-destructive)] border border-[var(--lf-border)]"
          data-testid="unsubscribe-continue-btn"
        >
          Continue cancelling
        </button>
        <button
          onClick={onKeep}
          className="lf-btn lf-btn-primary w-full"
          data-testid="unsubscribe-keep-btn"
        >
          <Smiley size={16} weight="fill" /> Keep my subscription
        </button>
      </div>

      <div className="mt-6 text-center text-xs text-[var(--lf-text-muted)]">
        Need help instead? <Link to="/profile" className="underline">Contact support</Link>.
      </div>
    </motion.div>
  );
}

function ReasonStage({
  expiresAt, reasonId, setReasonId, feedback, setFeedback,
  submitting, onBack, onSubmit,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="unsubscribe-stage-reason"
    >
      <h2 className="mt-5 text-lg font-bold">Mind sharing why? (optional)</h2>
      <p className="mt-1 text-sm text-[var(--lf-text-muted)]">
        It helps us improve. Pick one — or skip and just confirm.
      </p>

      <div className="mt-4 grid grid-cols-2 gap-2">
        {REASONS.map((r) => (
          <button
            key={r.id}
            onClick={() => setReasonId(r.id)}
            className={`rounded-2xl border p-3 text-left text-sm transition-all ${
              reasonId === r.id
                ? 'border-[var(--lf-primary)] bg-[#FFF5EE] ring-2 ring-[var(--lf-primary)]'
                : 'border-[var(--lf-border)] bg-[var(--lf-surface)]'
            }`}
            data-testid={`unsubscribe-reason-${r.id}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <label
        htmlFor="unsubscribe-feedback"
        className="mt-5 block text-xs uppercase tracking-widest text-[var(--lf-text-muted)]"
      >
        Anything else? (optional)
      </label>
      <textarea
        id="unsubscribe-feedback"
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        rows={3}
        maxLength={500}
        placeholder="What would have made LingoFlow better for you?"
        className="mt-2 w-full rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--lf-primary)]"
        data-testid="unsubscribe-feedback-input"
      />

      <div className="mt-3 rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-3 text-xs text-[var(--lf-text-muted)]">
        After confirming, you'll keep Pro access {expiresAt ? `through ${expiresAt}` : 'until the period ends'} and won't be charged again.
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={onBack}
          className="lf-btn lf-btn-ghost flex-1"
          data-testid="unsubscribe-back-btn"
        >
          Back
        </button>
        <button
          onClick={onSubmit}
          disabled={submitting}
          className="lf-btn lf-btn-destructive flex-[2]"
          data-testid="unsubscribe-confirm-btn"
        >
          {submitting ? (
            <SpinnerGap size={16} className="animate-spin" />
          ) : (
            <>
              <XCircle size={16} weight="fill" /> Confirm cancellation
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}

function DoneStage({ expiresAt, onHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="lf-card mt-10 p-6 text-center"
      data-testid="unsubscribe-stage-done"
    >
      <div className="grid h-16 w-16 mx-auto place-items-center rounded-full bg-[var(--lf-surface-muted)] text-[var(--lf-primary)]">
        <CheckCircle size={32} weight="duotone" />
      </div>
      <h2 className="mt-4 text-2xl font-bold">You're unsubscribed</h2>
      <p className="mt-2 text-sm text-[var(--lf-text-muted)]">
        Auto-renewal is off. You'll keep Pro access {expiresAt ? `through ${expiresAt}` : 'until the period ends'}, then drop to the Free plan automatically.
      </p>

      <div className="mt-5 rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-3 text-left text-xs text-[var(--lf-text-muted)]">
        <strong className="text-[var(--lf-text)]">Changed your mind?</strong>{' '}
        You can resubscribe at any time from{' '}
        <Link to="/pricing" className="underline">Pricing</Link>.
        Your vocabulary, streak, and skill history stay safe forever.
      </div>

      <div className="mt-6 space-y-2.5">
        <button
          onClick={onHome}
          className="lf-btn lf-btn-primary w-full"
          data-testid="unsubscribe-home-btn"
        >
          Back to Home
        </button>
        <Link
          to="/pricing"
          className="lf-btn lf-btn-ghost w-full"
          data-testid="unsubscribe-resub-btn"
        >
          Resubscribe later
        </Link>
      </div>
    </motion.div>
  );
}

function ImpactRow({ good, icon, title, copy }) {
  return (
    <li className="lf-card flex items-start gap-3 p-3.5">
      <span className={good ? 'text-[var(--lf-primary)]' : 'text-[var(--lf-text-muted)]'}>
        {icon}
      </span>
      <div>
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-[var(--lf-text-muted)]">{copy}</div>
      </div>
    </li>
  );
}
